import React, { useState, useEffect, CSSProperties } from 'react';

import countryCodeOptions from './countryCodes.json';
import { CountryISOCode, CountryOption } from './types';

interface PhoneInputComponentProps {
    channelCountryRegionISOCode: string; // Default country ISO code (IT, DE, US, ...)
    isCountryCodeHidden: boolean; // Config for disabling the country code selector, e.g for the US website
    phoneInputId: string; // For labels, and other html feature
    phoneValue?: string;
    defaultCountryCode?: CountryISOCode; // Optional custom country ISO code to preselect
    useDefaultCountryCode?: boolean; // Enable the defaultCountryCode logic (default: false)
    onPhoneNumberChange: (phoneNumber: string, fullPhoneNumber: string, hasValidationError: boolean) => void;
    resources: {
        phoneValidationErrorMin: string;
        phoneValidationErrorMax: string;
        phoneValidationErrorNumeric: string;
    };
}
const PhoneInputComponent: React.FC<PhoneInputComponentProps> = ({
    channelCountryRegionISOCode,
    isCountryCodeHidden,
    phoneInputId,
    phoneValue,
    defaultCountryCode: customCountryCode,
    useDefaultCountryCode = false,
    onPhoneNumberChange,
    resources
}) => {
    // Initialize selected country with priority: customCountryCode (if enabled) > channelCountryRegionISOCode > first in array
    const getInitialCountry = () => {
        // If useDefaultCountryCode is enabled and a custom country code is provided, use it
        if (useDefaultCountryCode && customCountryCode) {
            const found = (countryCodeOptions as CountryOption[]).find((c: CountryOption) => c.code === customCountryCode);
            if (found) {
                return found;
            }
        }

        // Otherwise try to use the channel country code
        const channelCountry = (countryCodeOptions as CountryOption[]).find((c: CountryOption) => c.code === channelCountryRegionISOCode);
        if (channelCountry) {
            return channelCountry;
        }

        // Fallback to first country in the array
        return (countryCodeOptions as CountryOption[])[0];
    };

    const [selectedCountry, setSelectedCountry] = useState(getInitialCountry());
    const [phoneNumber, setPhoneNumber] = useState(phoneValue || '');
    const [phoneNumberError, setPhoneNumberError] = React.useState('');

    const customStyles: { [key: string]: CSSProperties } = {
        container: {
            display: 'flex',
            flexDirection: 'row',
            padding: 0,
            width: '100%'
        },
        dropdown: {
            width: '30%',
            height: '47px',
            fontSize: '13px'
        },
        input: {
            width: '70%',
            height: '47px',
            fontSize: '14px'
        },
        option: {
            fontSize: '14px'
        }
    };

    // TODO: please test this part when you add phone number
    // input in profile information column
    useEffect(() => {
        if (phoneValue) {
            // If useDefaultCountryCode is enabled and customCountryCode is set, use that and strip any prefix from phoneValue
            if (useDefaultCountryCode && customCountryCode) {
                const customCountry = (countryCodeOptions as CountryOption[]).find((c: CountryOption) => c.code === customCountryCode);
                if (customCountry) {
                    setSelectedCountry(customCountry);
                    // Remove any existing country code prefix from phoneValue
                    const sortedCountries = [...(countryCodeOptions as CountryOption[])].sort(
                        (a: CountryOption, b: CountryOption) => b.dial_code.length - a.dial_code.length
                    );
                    const valueSanitized = phoneValue.replace(/\s+/g, '');
                    let phoneWithoutPrefix = phoneValue.replace(/\s+/g, '');
                    for (const country of sortedCountries) {
                        const countryDialSanitized = country.dial_code.replace(/\s+/g, '');
                        if (countryDialSanitized && valueSanitized.startsWith(countryDialSanitized)) {
                            phoneWithoutPrefix = valueSanitized.replace(countryDialSanitized, '');
                            break;
                        }
                    }
                    setPhoneNumber(phoneWithoutPrefix);
                    const validationResult = validatePhoneNumber(phoneWithoutPrefix);
                    if (validationResult) {
                        setPhoneNumberError(validationResult);
                    }
                    return;
                }
            }

            const detectCountryCode = (value: string) => {
                for (const country of countryCodeOptions as CountryOption[]) {
                    if (value.startsWith(country.dial_code)) {
                        return country;
                    }
                }
                return null;
            };

            const detectedCountry = detectCountryCode(phoneValue);
            if (detectedCountry) {
                setSelectedCountry(detectedCountry as CountryOption);
            }
            const defaultPhoneNumber = phoneValue.replace(detectedCountry?.dial_code || '', '');
            setPhoneNumber(defaultPhoneNumber);

            const validationResult = validatePhoneNumber(defaultPhoneNumber);

            if (validationResult) {
                setPhoneNumberError(validationResult);
            }
        }
    }, [phoneValue, useDefaultCountryCode, customCountryCode]);

    const validatePhoneNumber = (newPhoneNumber: string) => {
        const phoneRegex = /^\d{4,11}$/;

        if (newPhoneNumber.length && !phoneRegex.test(newPhoneNumber)) {
            if (newPhoneNumber.length < 4) {
                return resources.phoneValidationErrorMin;
            } else if (newPhoneNumber.length > 11) {
                return resources.phoneValidationErrorMax;
            } else {
                return resources.phoneValidationErrorNumeric;
            }
        }
        return false;
    };

    const handlePhoneNumberChange = (event: any) => {
        const newPhoneNumber = String(event.target.value);
        setPhoneNumber(newPhoneNumber);

        const newPhoneNumberFull = selectedCountry.dial_code + newPhoneNumber;
        const validationResult = validatePhoneNumber(newPhoneNumber);

        if (!validationResult) {
            setPhoneNumberError('');
        } else {
            setPhoneNumberError(validationResult);
        }

        onPhoneNumberChange(newPhoneNumber, newPhoneNumberFull, !!validationResult);
    };

    const handleCountryCodeChange = (event: any) => {
        const newCountryCode = String(event.target.value);
        const newSelectedCountry = (countryCodeOptions as CountryOption[]).find(c => c.dial_code === newCountryCode);
        if (newSelectedCountry) {
            setSelectedCountry(newSelectedCountry as CountryOption);
        }

        const newPhoneNumberFull = newCountryCode + phoneNumber;
        const validationResult = validatePhoneNumber(phoneNumber);

        onPhoneNumberChange(phoneNumber, newPhoneNumberFull, !!validationResult);
    };

    return (
        <>
            <div style={customStyles.container}>
                <select
                    style={customStyles.dropdown}
                    value={selectedCountry.dial_code}
                    disabled={isCountryCodeHidden}
                    onChange={handleCountryCodeChange}
                >
                    {!isCountryCodeHidden &&
                        countryCodeOptions.map((option, index) => (
                            <option key={index} value={option.dial_code} style={customStyles.option}>
                                {option.code} {option.dial_code}
                            </option>
                        ))}
                    {isCountryCodeHidden && (
                        <option value={selectedCountry.dial_code} style={customStyles.option}>
                            {selectedCountry.code} {selectedCountry.dial_code}
                        </option>
                    )}
                </select>

                <input
                    type='tel'
                    id={phoneInputId}
                    name={phoneInputId}
                    style={customStyles.input}
                    value={phoneNumber}
                    onChange={handlePhoneNumberChange}
                />
            </div>

            {phoneNumberError && (
                <div role='alert' aria-live='assertive' aria-hidden='true'>
                    <p id='text-error' style={{ color: '#C13533' }}>
                        {phoneNumberError}
                    </p>
                </div>
            )}
        </>
    );
};

export default PhoneInputComponent;

// Export the centralized phone country code input component
export { default as PhoneCountryCodeInput } from './phone-country-code-input';
